// src/pages/SignUp.js
import * as React from 'react';
import axios from 'axios';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

import AppTheme from '../shared-theme/AppTheme';
import { API_BASE } from '../lib/api';
import { ROLE_LABELS } from '../lib/roleLabels';
import ListSubheader from "@mui/material/ListSubheader";
import { authFieldSx } from './authFieldStyles';
import { passwordToggleSx } from './authPasswordToggleStyles';


const COUNTRY_OPTIONS = ['Egypt', 'Jordan', 'Morocco', 'Tunisia'];

const ROLE_GROUPS = [
  {
    key: 'primary',
    label: 'Primary Actors',
    roles: [
      { slug: 'farmers', label: 'Farmer' },
      { slug: 'consumers', label: 'Citizen / Consumer' },
    ],
  },
  {
    key: 'supply-chain',
    label: 'Supply Chain Actors',
    roles: [
      { slug: 'collectors', label: 'Collector' },
      { slug: 'cooperatives', label: 'Cooperative' },
      { slug: 'private_sector', label: 'Private Sector' },
      { slug: 'processors', label: 'Processor' },
      { slug: 'retailers', label: 'Retailer' },
      { slug: 'traders', label: 'Trader' },
    ],
  },
  {
    key: 'officials',
    label: 'Officials',
    roles: [
      { slug: 'policy_and_regulatory_bodies', label: 'Policy / Regulatory Body' },
      { slug: 'associations_and_federations', label: 'Association / Federation' },
    ],
  },
  {
    key: 'supporting',
    label: 'Supporting Roles',
    roles: [
      { slug: 'extension_agents', label: 'Extension Agent / Farm Advisor' },
      { slug: 'researcher', label: 'Researcher' },
    ],
  },
];

const buildRoleGroups = (roles = []) => {
  const rolesBySlug = new Map(roles.map((role) => [role.slug, role]));

  return ROLE_GROUPS.map((group) => ({
    ...group,
    items: group.roles
      .map(({ slug, label }) => {
        const role = rolesBySlug.get(slug);
        return role ? { ...role, displayName: ROLE_LABELS[slug] || label } : null;
      })
      .filter(Boolean),
  })).filter((group) => group.items.length > 0);
};

// const FALLBACK_ROLES = [
//   { id: 'farmer', name: 'Farmer', slug: 'farmer' },
//   { id: 'trader', name: 'Trader', slug: 'trader' },
//   { id: 'miller', name: 'Miller', slug: 'miller' },
//   { id: 'transporter', name: 'Transporter', slug: 'transporter' },
//   { id: 'gov_official', name: 'Gov Official', slug: 'gov_official' },
//   { id: 'ngo', name: 'NGO', slug: 'ngo' },
// ];

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: { width: '450px' },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignUpContainer = styled(Stack)(({ theme }) => ({
  minHeight: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  overflowY: 'auto',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: { padding: theme.spacing(4) },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

// helper για να πάρουμε το πρώτο μήνυμα απ' το array του DRF
const firstError = (v) => (Array.isArray(v) ? v[0] : v);
const RECAPTCHA_ACTION = "signup";
let recaptchaScriptPromise = null;

const loadRecaptchaEnterprise = (siteKey) => {
  if (window.grecaptcha?.enterprise) {
    return Promise.resolve(window.grecaptcha.enterprise);
  }
  if (!siteKey) {
    return Promise.reject(new Error("The security check is not configured."));
  }
  if (recaptchaScriptPromise) return recaptchaScriptPromise;

  recaptchaScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.grecaptcha?.enterprise) {
        resolve(window.grecaptcha.enterprise);
      } else {
        reject(new Error("The security check could not be loaded."));
      }
    };
    script.onerror = () => {
      recaptchaScriptPromise = null;
      script.remove();
      reject(new Error("The security check could not be loaded."));
    };
    document.head.appendChild(script);
  });

  return recaptchaScriptPromise;
};

const createRecaptchaToken = async (siteKey) => {
  const recaptcha = await loadRecaptchaEnterprise(siteKey);
  return new Promise((resolve, reject) => {
    recaptcha.ready(() => {
      recaptcha
        .execute(siteKey, { action: RECAPTCHA_ACTION })
        .then(resolve)
        .catch(() => reject(new Error("Security verification failed. Please try again.")));
    });
  });
};

export default function SignUp(props) {
  const navigate = useNavigate();

  // validation states
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [firstNameError, setFirstNameError] = React.useState(false);
  const [firstNameErrorMessage, setFirstNameErrorMessage] = React.useState('');
  const [lastNameError, setLastNameError] = React.useState(false);
  const [lastNameErrorMessage, setLastNameErrorMessage] = React.useState('');
  const [roleError, setRoleError] = React.useState(false);
  const [roleErrorMessage, setRoleErrorMessage] = React.useState('');
  const [countryError, setCountryError] = React.useState(false);
  const [countryErrorMessage, setCountryErrorMessage] = React.useState('');

  // data
  const [roles, setRoles] = React.useState([]);
  const [selectedRole, setSelectedRole] = React.useState('');
  const [country, setCountry] = React.useState('');
  const [legalAccepted, setLegalAccepted] = React.useState(false);
  const [recaptchaConfig, setRecaptchaConfig] = React.useState(null);
  const [recaptchaConfigError, setRecaptchaConfigError] = React.useState("");

  // feedback
  const [submitting, setSubmitting] = React.useState(false);
  const [apiError, setApiError] = React.useState('');
  const [apiSuccess, setApiSuccess] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);



  React.useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/roles/`);
        console.log(res.data);
        if (Array.isArray(res.data) && res.data.length > 0) setRoles(res.data);
      } catch {
        /* fallback στους στατικούς ρόλους */
      }
    })();
  }, []);


  React.useEffect(() => {
    let active = true;
    axios
      .get(`${API_BASE}/recaptcha-config`)
      .then((response) => {
        if (!active) return;
        setRecaptchaConfig({
          enabled: Boolean(response.data?.enabled),
          siteKey: response.data?.site_key || "",
        });
        setRecaptchaConfigError("");
      })
      .catch(() => {
        if (!active) return;
        setRecaptchaConfig(null);
        setRecaptchaConfigError(
          "The security check is unavailable. Please refresh the page."
        );
      });

    return () => {
      active = false;
    };
  }, []);
  const groupedRoles = React.useMemo(() => buildRoleGroups(roles), [roles]);
  const roleLabelByValue = React.useMemo(() => {
    const entries = groupedRoles.flatMap((group) =>
      group.items.map((role) => [role.slug || role.id, role.displayName])
    );
    return Object.fromEntries(entries);
  }, [groupedRoles]);
  const signupSelectLabelSx = {
    color: "text.secondary",
    "&.Mui-focused": { color: "text.secondary" },
    "&.Mui-error": { color: "text.secondary" },
  };
  const signupSelectMenuSx = {
    bgcolor: "#fff",
    border: "1px solid #e8e8e8",
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
    "& .MuiMenuItem-root": {
      outline: "none",
      backgroundColor: "#fff !important",
      "&:hover, &.Mui-focusVisible": {
        outline: "none",
        backgroundColor: "#fffaf0 !important",
      },
      "&.Mui-selected": {
        backgroundColor: "#fff7d6 !important",
      },
      "&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
        outline: "none",
        backgroundColor: "#ffefb0 !important",
      },
    },
  };

  const ROLE_MENU_PROPS = {
    PaperProps: {
      sx: signupSelectMenuSx,
      style: {
        maxHeight: 48 * 6 + 12, // περίπου 6 επιλογές ορατές
      },
    },
    disableScrollLock: true,
  };
  const COUNTRY_MENU_PROPS = {
    PaperProps: {
      sx: signupSelectMenuSx,
    },
    disableScrollLock: true,
  };


  const validateInputs = () => {
    const email = document.getElementById('email');
    const password = document.getElementById('password');
    const firstName = document.getElementById('first_name');
    const lastName = document.getElementById('last_name');
    let isValid = true;

    if (!email?.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password?.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    const firstNameValue = firstName?.value?.trim() || '';
    if (!firstNameValue) {
      setFirstNameError(true);
      setFirstNameErrorMessage('Please enter your first name.');
      isValid = false;
    } else if (firstNameValue.length < 3) {
      setFirstNameError(true);
      setFirstNameErrorMessage('First name must be at least 3 characters long.');
      isValid = false;
    } else {
      setFirstNameError(false);
      setFirstNameErrorMessage('');
    }

    const lastNameValue = lastName?.value?.trim() || '';
    if (!lastNameValue) {
      setLastNameError(true);
      setLastNameErrorMessage('Please enter your last name.');
      isValid = false;
    } else if (lastNameValue.length < 3) {
      setLastNameError(true);
      setLastNameErrorMessage('Last name must be at least 3 characters long.');
      isValid = false;
    } else {
      setLastNameError(false);
      setLastNameErrorMessage('');
    }

    if (!selectedRole) {
      setRoleError(true);
      setRoleErrorMessage('Please select a role.');
      isValid = false;
    } else {
      setRoleError(false);
      setRoleErrorMessage('');
    }

    if (!country) {
      setCountryError(true);
      setCountryErrorMessage('Please select a country.');
      isValid = false;
    } else {
      setCountryError(false);
      setCountryErrorMessage('');
    }

    if (!legalAccepted) {
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setApiError('');
    setApiSuccess('');

    if (!validateInputs()) return;
    if (!recaptchaConfig) {
      setApiError(
        recaptchaConfigError || "The security check is still loading. Please try again."
      );
      return;
    }

    const data = new FormData(event.currentTarget);
    const first_name = (data.get('first_name') || '').trim();
    const last_name = (data.get('last_name') || '').trim();
    const email = (data.get('email') || '').trim();
    const password = data.get('password');

    const payload = {
      first_name,
      last_name,
      username: email,
      email,
      password,
      password2: password,
      role: selectedRole,
      location: country,
    };




    try {
      setSubmitting(true);
      if (recaptchaConfig.enabled) {
        payload.recaptcha_token = await createRecaptchaToken(
          recaptchaConfig.siteKey
        );
      }
      const res = await axios.post(`${API_BASE}/register`, payload);
      if (res.status === 200 || res.status === 201) {
        setApiSuccess('Registration successful! Check your email for the 6-digit code.');
        setTimeout(() => navigate(`/verify-otp?email=${encodeURIComponent(email)}`), 600);
      } else {
        setApiError('Registration failed. Please try again.');
      }
    } catch (err) {
      // Πιάνουμε field-level errors από DRF (π.χ. { email: ["Email already exists."] })
      const data = err?.response?.data;

      // καθάρισε τυχόν προηγούμενα λάθη πεδίων
      setEmailError(false); setEmailErrorMessage('');
      setFirstNameError(false); setFirstNameErrorMessage('');
      setLastNameError(false); setLastNameErrorMessage('');
      setPasswordError(false); setPasswordErrorMessage('');
      setApiError('');

      if (data && typeof data === 'object') {
        let shown = false;

        if (data.email) {
          setEmailError(true);
          setEmailErrorMessage(firstError(data.email));
          shown = true;
        }
        if (data.username) {
          // username=email → δείξε το και στο email input
          setEmailError(true);
          setEmailErrorMessage(firstError(data.username));
          shown = true;
        }
        if (data.first_name) {
          setFirstNameError(true);
          setFirstNameErrorMessage(firstError(data.first_name));
          shown = true;
        }
        if (data.last_name) {
          setLastNameError(true);
          setLastNameErrorMessage(firstError(data.last_name));
          shown = true;
        }
        if (data.password) {
          setPasswordError(true);
          setPasswordErrorMessage(firstError(data.password));
          shown = true;
        }
        if (data.role) {
          setRoleError(true);
          setRoleErrorMessage(firstError(data.role));
          shown = true;
        }
        if (data.location) {
          setCountryError(true);
          setCountryErrorMessage(firstError(data.location));
          shown = true;
        }
        if (data.recaptcha) {
          setApiError(firstError(data.recaptcha));
          shown = true;
        }

        if (!shown) {
          const generic = data.detail || data.error;
          setApiError(generic ? String(generic) : 'Registration failed. Please check your inputs.');
        }
      } else {
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          'Something went wrong. Please try again.';
        setApiError(String(msg));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <SignUpContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <img src="/images/seedsLogo.png" alt="SEEDS Logo" style={{ maxHeight: 80, objectFit: 'contain' }} />
          </Box>

          <Typography component="h1" variant="h4" sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)', textAlign: 'center' }}>
            Sign up
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl>
              <FormLabel htmlFor="first_name">First Name</FormLabel>
              <TextField
                id="first_name"
                name="first_name"
                autoComplete="given-name"
                required
                fullWidth
                placeholder="First name"
                error={firstNameError}
                helperText={firstNameErrorMessage}
                color={firstNameError ? 'error' : 'primary'}
                sx={authFieldSx}
              />
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="last_name">Last Name</FormLabel>
              <TextField
                id="last_name"
                name="last_name"
                autoComplete="family-name"
                required
                fullWidth
                placeholder="Last name"
                error={lastNameError}
                helperText={lastNameErrorMessage}
                color={lastNameError ? 'error' : 'primary'}
                sx={authFieldSx}
              />
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="email">Email</FormLabel>
              <TextField
                required
                fullWidth
                id="email"
                name="email"
                autoComplete="email"
                placeholder="your@email.com"
                error={emailError}
                helperText={emailErrorMessage}
                color={emailError ? 'error' : 'primary'}
                sx={authFieldSx}
              />
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="password">Password</FormLabel>
              <TextField
                required
                fullWidth
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="••••••"
                error={passwordError}
                helperText={passwordErrorMessage}
                color={passwordError ? 'error' : 'primary'}
                sx={authFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        edge="end"
                        onClick={() => setShowPassword((value) => !value)}
                        onMouseDown={(event) => event.preventDefault()}
                        sx={passwordToggleSx}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon fontSize="small" />
                        ) : (
                          <VisibilityOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            <FormControl error={roleError} fullWidth>
              <FormLabel id="role-label" htmlFor="role" sx={signupSelectLabelSx}>Role</FormLabel>
              <Select
                id="role"
                labelId="role-label"
                displayEmpty
                IconComponent={KeyboardArrowDownRoundedIcon}
                value={selectedRole}
                onChange={(e) => {
                  setSelectedRole(e.target.value);
                  setRoleError(false);
                  setRoleErrorMessage('');
                }}
                renderValue={(value) =>
                  value ? roleLabelByValue[value] || value : (
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      Select role
                    </Box>
                  )
                }
                MenuProps={ROLE_MENU_PROPS}
                sx={authFieldSx}
              >
                {groupedRoles.flatMap(({ key, label, items }) => [
                  <ListSubheader
                    key={`category-${key}`}
                    disableSticky
                    sx={{ fontWeight: 700, color: 'text.primary', lineHeight: '36px' }}
                  >
                    {label}
                  </ListSubheader>,
                  ...items.map((r) => (
                    <MenuItem
                      key={r.slug || r.id}
                      value={r.slug || r.id}
                      sx={{ pl: 3 }}
                    >
                      {r.displayName}
                    </MenuItem>
                  )),
                ])}

              </Select>
              {roleError && <FormHelperText>{roleErrorMessage}</FormHelperText>}
            </FormControl>

            <FormControl error={countryError} fullWidth>
              <FormLabel id="country-label" htmlFor="country" sx={signupSelectLabelSx}>Country</FormLabel>
              <Select
                id="country"
                labelId="country-label"
                displayEmpty
                IconComponent={KeyboardArrowDownRoundedIcon}
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                  setCountryError(false);
                  setCountryErrorMessage('');
                }}
                MenuProps={COUNTRY_MENU_PROPS}
                renderValue={(value) =>
                  value || (
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      Select country
                    </Box>
                  )
                }
                sx={authFieldSx}
              >
                {COUNTRY_OPTIONS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
              {countryError && <FormHelperText>{countryErrorMessage}</FormHelperText>}
            </FormControl>

            <FormControlLabel
              sx={{
                alignItems: "flex-start",
                m: 0,
                "& .MuiFormControlLabel-label": { pt: 0.35 },
              }}
              control={
                <Checkbox
                  checked={legalAccepted}
                  onChange={(event) => setLegalAccepted(event.target.checked)}
                  inputProps={{ "aria-label": "Accept Privacy Policy and Terms of Use" }}
                  sx={{
                    "&&": {
                      width: 22,
                      height: 22,
                      m: 0.5,
                      mr: 1,
                      flexShrink: 0,
                      color: "#171717",
                      backgroundColor: "#fff",
                      border: "2px solid #f7b900",
                      borderRadius: "6px",
                      boxShadow: "none",
                    },
                    "&&:hover": {
                      backgroundColor: "#fff7d6",
                      borderColor: "#d99b00",
                    },
                    "&&.Mui-checked": {
                      color: "#171717",
                      backgroundColor: "#f7b900",
                      borderColor: "#f7b900",
                    },
                    "&&.Mui-checked:hover": {
                      backgroundColor: "#d99b00",
                      borderColor: "#d99b00",
                    },
                    "& .MuiSvgIcon-root": {
                      width: 18,
                      height: 18,
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, color: "text.secondary" }}>
                  I have read and agree to the{" "}
                  <Link
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    underline="none"
                    sx={{ color: "#9a7000", fontWeight: 700, textDecoration: "none", "&:hover": { textDecoration: "none" } }}
                  >
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    href="/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    underline="none"
                    sx={{ color: "#9a7000", fontWeight: 700, textDecoration: "none", "&:hover": { textDecoration: "none" } }}
                  >
                    Terms of Use
                  </Link>
                  .
                </Typography>
              }
            />
            {recaptchaConfig?.enabled && (
              <Typography sx={{ fontSize: 11.5, lineHeight: 1.5, color: "text.secondary" }}>
                Protected by reCAPTCHA. The Google{" "}
                <Link
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  sx={{ display: "inline-block", color: "#9a7000", fontWeight: 600, "&:hover": { textDecoration: "none" } }}
                >
                  Privacy Policy
                </Link>{" "}
                and{" "}
                <Link
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  sx={{ display: "inline-block", color: "#9a7000", fontWeight: 600, "&:hover": { textDecoration: "none" } }}
                >
                  Terms of Service
                </Link>{" "}
                apply.
              </Typography>
            )}
            {recaptchaConfigError && <Alert severity="error">{recaptchaConfigError}</Alert>}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitting || !legalAccepted || !recaptchaConfig || Boolean(recaptchaConfigError)}
              sx={{
                "&&": {
                  backgroundColor: "#f7b900",
                  backgroundImage: "none",
                  color: "#171717",
                  borderColor: "#f7b900",
                  boxShadow: "none",
                },
                "&&:hover, &&:focus-visible": {
                  backgroundColor: "#d99b00",
                  backgroundImage: "none",
                  color: "#171717",
                  borderColor: "#d99b00",
                  boxShadow: "none",
                },
                "&&.Mui-disabled": {
                  backgroundColor: "#f4e5ad",
                  backgroundImage: "none",
                  color: "rgba(23, 23, 23, 0.55)",
                  borderColor: "#f4e5ad",
                },
                fontWeight: 700,
              }}
            >
              {submitting ? 'Submitting…' : 'Sign up'}
            </Button>

            {apiError && <Alert severity="error">{apiError}</Alert>}
            {apiSuccess && <Alert severity="success">{apiSuccess}</Alert>}
          </Box>

          <Typography sx={{ pt: 1, textAlign: 'center', fontSize: 14 }}>
            <Link
              component={RouterLink}
              to="/signin"
              underline="none"
              sx={{ color: '#9a7000', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'none' } }}
            >
              Already have an account? Sign In
            </Link>
          </Typography>
        </Card>
      </SignUpContainer>
    </AppTheme>
  );
}
